import { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthContext from '../../contexts/auth-context.js';
import { ThemeContext } from '../../components/molecules/Nav.js';
import moment from 'moment';
import { 
  generateGameDayDummyData, 
  USE_DUMMY_OCCUPANCY_DATA 
} from "../../util.js";

import {
  formatTimestamp,
  generateDay,
  findIntelligentCutoff,
  roundToNextHour,
} from '../../util.js';
import { getMostRecentSeriesAndGame } from './helper/helper_functions.js';
import { getRetailAPI } from './dummyLiveData.js';

const RetailLocationController = ({ children }) => {
  const location = useLocation();
  const { state } = location;
  const id = state?.id;
  const currentLocation = state?.currentLocation;
  const navigate = useNavigate();
  // Same as util's getQueuesAPI unless dummy data mode is on (local dev only)
  const getQueuesAPI = getRetailAPI(currentLocation);

  const authCtx = useContext(AuthContext);
  const [theme, setTheme] = useContext(ThemeContext);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [games, setGames] = useState([]);
  const [zones, setZones] = useState([]);
  const [now, setNow] = useState(
    new Date(new Date().toLocaleString('en-US', { timeZone: currentLocation.timeZone }))
  ); // Set the current date/time to the location's timezone current date/time
  const [currentSeason, setCurrentSeason] = useState(null);
  const [currentSeries, setCurrentSeries] = useState(null);
  const [currentGame, setCurrentGame] = useState(null);
  const [currentSection, setCurrentSection] = useState(currentLocation.sectionTypes.MAP);
  const [currentTime, setCurrentTime] = useState(currentLocation.doorsOpenTime);
  const [dayType, setDayType] = useState(currentLocation.dayTypes.GAME);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  // Add a ref to store the dummy data
  const dummyDataRef = useRef(null);
  const gamesRequestInFlight = useRef(false);
  const [fetchNonce, setFetchNonce] = useState(0);
  const [peak, setPeak] = useState(null);
  const [avg, setAvg] = useState(null);

  const [sort, setSort] = useState("zone-id");
  const [showAllMaps, setShowAllMaps] = useState(false);
  const [isToday, setIsToday] = useState(false);
  // Used to auto refresh counter
  const [autoFetchEnabled, setAutoFetchEnabled] = useState(false);
  const intervalRef = useRef(null);
  // Used to auo refresh occupancy data
  const [occupancyAutoFetchEnabled, setOccupancyAutoFetchEnabled] = useState(false);
  const intervalRefOccupancy = useRef(null);
  // Used to notify RetailMap to use the new occupancy value we just fetched
  const [occupancyAutoFetchDidComplete, setOccupancyAutoFetchDidComplete] = useState(false);

  const [backgroundLoading, setBackgroundLoading] = useState(false);

  const [regularDayCount, setRegularDayCount] = useState(0);
  const [counterDataAvailable, setCounterDataAvailable] = useState(true);
  const [pregameCount, setPregameCount] = useState(0);
  const [gatesOpenCount, setGatesOpenCount] = useState(0);
  const [firstPitchCount, setFirstPitchCount] = useState(0);
  const [counterSensorInfoData, setCounterSensorInfoData] = useState({}); // Contains the last count and sensor info use for counter view

  const [counterTrafficDataList, setCounterTrafficDataList] = useState([]);
  const [overviewTrafficDataList, setOverviewTrafficDataList] = useState([]);
  const [overviewSelectedDates, setOverviewSelectedDates] = useState([]);
  const [overviewGameTitlesList, setOverviewGameTitlesList] = useState([]);

  const [queueTimeData, setQueueTimeData] = useState([]);
  const [queueTimeDataMiniMap, setQueueTimeDataMiniMap] = useState({});
  const [transactionTimeData, setTransactionTimeData] = useState({});
  const [transactionTimeDataMiniMap, setTransactionTimeDataMiniMap] = useState({});

  // Shared zone labels fetched from Mongo locations collection
  const [zoneLabels, setZoneLabels] = useState({});

  // Processes the counter data for the location
  // Data is ALL sensor counts and ids
  // Pregame Gates and First Pitch counts are calculated from all sensors except sale Id sensors
  // Specific info counters are the above ^ counts but with details of each sensors count
  // Sales counts are only counts related to sale sensors ids
  const processCounterData = useCallback((data) => {
    if (!currentGame) return;

    // We dont necessarily need pregameStart
    const { gatesOpen, gameStartTime } = currentGame;
    const gameDate = currentGame.date; // Should be YYYY-MM-DD format
    let tempPregameCount = 0;
    let tempGatesOpenCount = 0;
    let tempFirstPitchCount = 0;

    // slice(1) to skip first entry which is always a count and time of 0
    // TODO cleanup and use actual dates as our order not time stamp only
    // This is ok for now cus backend returns proper data only from 8am to 3am using window calculations
    data.slice(1).forEach((entry) => {
      const [datePart, timePart] = entry.timestamp.split("T");
      const entryTime = timePart.slice(0, 5);
      const entryHour = parseInt(timePart.split(":")[0]);

      // Only process entries from the game date OR early morning of next day (before 3 AM)
      const isGameDay = datePart === gameDate;
      const isEarlyNextDay = datePart > gameDate && entryHour < 3;

      // Skip entries that are outside our game day window
      if (!isGameDay && !isEarlyNextDay) return;

      // Early next day entries (12:00 AM - 2:59 AM) should be counted as firstPitch
      if (isEarlyNextDay) {
        tempFirstPitchCount = Math.abs(
          tempPregameCount + tempGatesOpenCount - entry.count
        );
        return;
      }

      // ANY door count recorded BEFORE gates open MUST be a count for pregame
      // We can assume this because all entries 12:00am to 2:59am will have been counted
      // under first pitch already
      if (entryTime < gatesOpen) {
        tempPregameCount = entry.count;
      }
      // If entry is after gates open but before game start time
      else if (entryTime >= gatesOpen && entryTime < gameStartTime) {
        tempGatesOpenCount = Math.abs(tempPregameCount - entry.count);
      }
      // If entry is after game start time
      else if (entryTime >= gameStartTime) {
        tempFirstPitchCount = Math.abs(
          tempPregameCount + tempGatesOpenCount - entry.count
        );
      }
    });

    // We need to map the last sensor specific counts to their info for counter view
    const sensorSpecificFinalResults = {};
    for (const key in data[data.length - 1]) {
      if (currentLocation.doorCounterSensorInfo.hasOwnProperty(key)) {
        sensorSpecificFinalResults[key] = {
          ...currentLocation.doorCounterSensorInfo[key],
          count: data[data.length - 1][key],
          countOut: data[data.length - 1][`${key}_out`] || 0
        };
      }
    }

    setCounterSensorInfoData(sensorSpecificFinalResults);
    setPregameCount(tempPregameCount);
    setGatesOpenCount(tempGatesOpenCount);
    setFirstPitchCount(tempFirstPitchCount);
  }, [currentGame, currentLocation]);

  const fetchData = (isBackgroundUpdate = false) => {

    // First if it ISNT TODAY auto fetch disabled
    // Mainly used for RetailLocation.js to know which loading (spinner vs linear) to use
    if(!isToday) {
      setOccupancyAutoFetchEnabled(false);
    }
    // Promise to prevent race conditions and ensure better state management
    return new Promise((resolve, reject) => {
      if (!currentLocation || !selectedDate) {
        // console.log('Missing required data for fetch');
        reject(new Error('Missing required data'));
        return;
      }
  
      // console.log('Starting fetch for:', moment(selectedDate).format("yyyy-MM-DD"));
      // Make sure we let RetailMap know that fetch hasn't completed yet
      setOccupancyAutoFetchDidComplete(false);
      // Only set loading true if this is not a background update
      if (!isBackgroundUpdate) {
        setLoading(true);
      } else {
        setBackgroundLoading(true);
      }
      
      // Format our selected date for consistency
      const selectedDateFormatted = moment(selectedDate).format("YYYY-MM-DD");
      
      // Create an array to store our promises
      const fetchPromises = [];
      // Promise for zone data
      // Promise for zone data - UPDATED TO V2
      const zoneDataPromise = new Promise((resolveZone, rejectZone) => {
        getQueuesAPI(
          "get-retail-location-daily-data-v2",  // <-- Changed to v2
          { 
            timezone: currentLocation.timeZone,
            zones: currentLocation.zones,  // <-- Pass full zone config (not zone IDs)
            venue: authCtx.venue,
            locationId: currentLocation.id,
            measurement: currentLocation.measurement,
            date: selectedDateFormatted 
          },
          (data_, error) => {
            if (error) {
              if (error.status === 401) {
                authCtx.logout();
                navigate("/auth");
              } else {
                alert("Unable to fetch data. Please refresh the page and try again.");
              }
              rejectZone(error);
            } else {
              try {
                // Map to hold all our zone data
                // zone id = key
                const zoneDataMap = new Map();
                
                // Search through all data_ entries to find zone data
                data_.forEach((dataEntry, index) => {
                  Object.entries(dataEntry).forEach(([zoneId, zoneData]) => {
                    zoneDataMap.set(parseInt(zoneId), zoneData);
                  });
                });
                
                // Process zone data
                const zones_ = currentLocation.zones.map((currentZone) => {
                  const zoneId = currentZone.id;
                  
                  // Get data for this zone from our map
                  const currentData = zoneDataMap.get(zoneId) || [];

                  const formattedData = currentData.map((item, timeIndex) => ({
                    zone: zoneId,
                    time: timeIndex,
                    time2: item?.time || "",
                    traffic: item?.occupancy || 0,
                  }));

                  const peak = formattedData.reduce(
                    (max, item) => (item.traffic > max.traffic ? item : max),
                    { traffic: -Infinity }
                  );

                  return {
                    zoneId,
                    zoneData: formattedData,
                    label: currentZone.label,
                    x: currentZone.x,
                    y: currentZone.y,
                    redDotX: currentZone.circleX,
                    redDotY: currentZone.circleY,
                    peak,
                  };
                });

                const day = generateDay(selectedDate);
                
                const timeSeriesMap = new Map(
                  zones_.map(zone => [
                    zone.zoneId,
                    new Map(zone.zoneData.map(d => [d.time2, d]))
                  ])
                );

                const processed = day.reduce((acc, currentMinute, x) => {
                  const time = new Date(currentMinute);
                  const formattedTime = formatTimestamp(time);

                  const minuteData = zones_.map(zone => {
                    const zoneTimeSeries = timeSeriesMap.get(zone.zoneId);
                    const match = zoneTimeSeries?.get(formattedTime);

                    return {
                      traffic: match?.traffic || 0,
                      id: zone.zoneId
                    };
                  });

                  const total = minuteData.reduce((sum, item) => sum + item.traffic, 0);
                  const perc = Math.round((total / currentLocation.maxOccupancy) * 100);
                  
                  acc.push({
                    time: x,
                    time2: currentMinute,
                    total,
                    perc,
                    traffic: minuteData,
                  });

                  return acc;
                }, []);

                zones_.sort((x, y) => {
                  switch (sort) {
                    case "zone-id":
                      return x.zoneId - y.zoneId;
                    case "current":
                      return (
                        (y.zoneData[currentTime]?.traffic || 0) -
                        (x.zoneData[currentTime]?.traffic || 0)
                      );
                    case "peak":
                      return y.peak.traffic - x.peak.traffic;
                    default:
                      return 0;
                  }
                });

                setZones(zones_);
                setData(processed);
                resolveZone({ zones: zones_, data: processed });
              } catch (error) {
                console.error('Error processing data:', error);
                rejectZone(error);
              }
            }
          },
          authCtx.token
        );
      });
       // Combine sales and stairs sensor IDs for exclusion
      const excludedSensorIds = [
        ...(currentLocation.doorCounterSensorSalesIds || []),
        ...(currentLocation.doorCounterSensorStairsIds || [])
      ];

      // Promise for door count data
      const doorCountPromise = new Promise((resolveDoor, rejectDoor) => {
        getQueuesAPI(
          "get-location-daily-door-count-v2",
          {
            timezone: currentLocation.timeZone,
            venue: authCtx.venue,
            locationId: currentLocation.id,
            excludedSensorIds: excludedSensorIds, // will usually and almost always be sales related counter ids (unless they have stairs)
            measurement: currentLocation.measurement,
            doorMeasurement: currentLocation.doorMeasurement,
            date: selectedDateFormatted,
          },
          (data, error) => {
            if (!data) {
              console.error("Failed to fetch daily door count");
              resolveDoor(null); // Resolve with null instead of rejecting
              return;
            }
            
            const dataDate = data[data.length - 1]?.timestamp?.split("T")[0];
            
            // Hardcoded check for showing counter data before automatic counter for regular day
            if (selectedDateFormatted < "2025-04-06" && authCtx.venue === "braves" && dayType === "regular") {
              setCounterDataAvailable(false);
            } 
            // Braves game days before the automatic counter (Oct 2024) have no counter data
            else if (selectedDateFormatted < "2024-10-01" && authCtx.venue === "braves" && dayType === "game") {
              setCounterDataAvailable(false);
            }
            else {
              // Logic for Counter View
              setCounterDataAvailable(true);
              // Process API data as normal
              if (dayType === "game") {
                // Specialized processing for game days
                processCounterData(data);
              }
              if (dayType === "regular") {
                // just get the last entries count
                setRegularDayCount(data[data.length - 1].count);

                // Build per-sensor last-count map so conversion can render on regular days.
                // Mirrors the sensor-mapping step in processCounterData, without the
                // game-phase bucketing (which requires a currentGame).
                const sensorSpecificFinalResults = {};
                for (const key in data[data.length - 1]) {
                  if (currentLocation.doorCounterSensorInfo.hasOwnProperty(key)) {
                    sensorSpecificFinalResults[key] = {
                      ...currentLocation.doorCounterSensorInfo[key],
                      count: data[data.length - 1][key],
                      countOut: data[data.length - 1][`${key}_out`] || 0
                    };
                  }
                }
                setCounterSensorInfoData(sensorSpecificFinalResults);
              }
              setCounterTrafficDataList([data]);
            }
          
            // Set data for the chart regardless of source
            setOverviewTrafficDataList([data]);
            setOverviewSelectedDates([selectedDate]);
            setOverviewGameTitlesList(["Traffic Data"]);
            
            resolveDoor(data);
          },
          authCtx.token
        );
      });

      // Promise for queue time data
      const queueTimePromise = new Promise((resolveQueue, rejectQueue) => {
        if (!(currentLocation.queueTimeSensorIds || []).length && !currentLocation.queueZone) {
          resolveQueue(null);
          return;
        }

        getQueuesAPI(
          "get-retail-location-daily-queue-time",
          {
            timezone: currentLocation.timeZone,
            venue: authCtx.venue,
            locationId: currentLocation.id,
            measurement: currentLocation.measurement,
            queueMeasurement: currentLocation.queueMeasurement,
            queueTimeSensorIds: currentLocation.queueTimeSensorIds,
            date: selectedDateFormatted,
          },
          (data, error) => {
            if (!data) {
              console.error("Failed to fetch daily queue time");
              resolveQueue(null);
              return;
            }
            
            // Transform to queueLocationId-keyed object for QueueChart
            // Input:  [{ timestamp: "...", queueTime: { 1: 3 } }, ...]
            // Output: { 1: [{ timestamp: "...", queueTime: 3 }, ...] }
            const chartData = {};
            
            // Transform to minute-keyed object for MiniMapZone
            // Output: { 660: { 1: 3 }, 661: { 1: 2 }, ... }
            const miniMapData = {};
            
            data.forEach((entry) => {
              const { timestamp, queueTime } = entry;
              
              // Parse minute of day
              const timePart = timestamp.split('T')[1];
              const [hours, minutes] = timePart.split(':').map(Number);
              const minuteOfDay = hours * 60 + minutes;
              
              // Build minimap format
              miniMapData[minuteOfDay] = queueTime;
              
              // Build chart format
              Object.entries(queueTime).forEach(([queueLocationId, time]) => {
                const id = parseInt(queueLocationId);
                
                if (!chartData[id]) {
                  chartData[id] = [];
                }
                
                chartData[id].push({
                  timestamp,
                  queueTime: time
                });
              });
            });
            
            setQueueTimeData(chartData);
            setQueueTimeDataMiniMap(miniMapData);
            resolveQueue(data);
          },
          authCtx.token
        );
      });

      // Promise for transaction time data
      const transactionTimePromise = new Promise((resolveTransaction, rejectTransaction) => {
        if (!currentLocation.transactionMeasurement) {
          resolveTransaction(null);
          return;
        }
        
        getQueuesAPI(
          "get-retail-location-daily-transaction-time",
          {
            timezone: currentLocation.timeZone,
            venue: authCtx.venue,
            locationId: currentLocation.id,
            measurement: currentLocation.measurement,
            transactionMeasurement: currentLocation.transactionMeasurement,
            date: selectedDateFormatted,
          },
          (data, error) => {
            if (!data) {
              console.error("Failed to fetch daily transaction time");
              resolveTransaction(null);
              return;
            }
            
            // Transform to txLocationId-keyed object for TransactionChart
            // Input:  [{ timestamp: "...", transactionTime: { 1: 180, 2: 120 } }, ...]
            // Output: { 1: [{ timestamp: "...", transactionTime: 180 }, ...], 2: [...] }
            const chartData = {};
            
            // Transform to minute-keyed object for MiniMapZone
            // Output: { 660: { 1: 180, 2: 120 }, ... }
            const miniMapData = {};
            
            data.forEach((entry) => {
              const { timestamp, transactionTime } = entry;
              
              // Parse minute of day
              const timePart = timestamp.split('T')[1];
              const [hours, minutes] = timePart.split(':').map(Number);
              const minuteOfDay = hours * 60 + minutes;
              
              // Build minimap format
              miniMapData[minuteOfDay] = transactionTime;
              
              // Build chart format
              Object.entries(transactionTime).forEach(([txLocationId, time]) => {
                const id = parseInt(txLocationId);
                
                if (!chartData[id]) {
                  chartData[id] = [];
                }
                
                chartData[id].push({
                  timestamp,
                  transactionTime: time
                });
              });
            });
            
            setTransactionTimeData(chartData);
            setTransactionTimeDataMiniMap(miniMapData);
            resolveTransaction(data);
          },
          authCtx.token
        );
      });
      
      // Add our promises to the array
      fetchPromises.push(zoneDataPromise);
      fetchPromises.push(doorCountPromise);
      fetchPromises.push(queueTimePromise);
      fetchPromises.push(transactionTimePromise);

      // Wait for all promises to resolve
      Promise.all(fetchPromises)
        .then(([zoneResult, doorResult, queueResult, transactionResult]) => {
          // Unused results above, we can potentially optimize this controller to use the results
          // rather than just directly modify and set our states in the api call data response.

          // === DUMMY DATA OVERRIDE ===
          if (USE_DUMMY_OCCUPANCY_DATA) {
            // Only generate once per date/game combination
            const cacheKey = `${selectedDateFormatted}-${currentGame?.date}`;
            if (!dummyDataRef.current || dummyDataRef.current.key !== cacheKey) {
              dummyDataRef.current = {
                key: cacheKey,
                data: generateGameDayDummyData(currentLocation, currentGame, selectedDate)
              };
            }
            setZones(dummyDataRef.current.data.zones);
            setData(dummyDataRef.current.data.processed);
          }
          // === END DUMMY DATA ===

          if (!isBackgroundUpdate) {
            setLoading(false);
          } else {
            setBackgroundLoading(false);
          }
          
          // console.log('Data fetch completed successfully');
          // Notify RetailMap that we now have our new data point
          setOccupancyAutoFetchDidComplete(true);
          resolve({ zones: zoneResult?.zones, data: zoneResult?.data });
        })
        .catch(error => {
          if (!isBackgroundUpdate) {
            setLoading(false);
          } else {
            setBackgroundLoading(false);
          }
          console.error('Error fetching data:', error);
          reject(error);
        });
    });
  };

  // Auto Refresh functionality for Occupancy and Door Count Data
  useEffect(() => {
    if (intervalRefOccupancy.current) {
      clearInterval(intervalRefOccupancy.current);
    }
    
    if (isToday && (occupancyAutoFetchEnabled || autoFetchEnabled)) {
      intervalRefOccupancy.current = setInterval(() => {
        // console.log('Auto-fetching occupancy and door count data...');
        fetchData(true); // Pass true to indicate this is a background update
      }, 60000); // 1 minute
    }
    
    return () => {
      if (intervalRefOccupancy.current) {
        clearInterval(intervalRefOccupancy.current);
      }
    };
  }, [isToday, occupancyAutoFetchEnabled, autoFetchEnabled]);

  const getGames = () => {
    // One schedule request at a time (several effects ask for it on load)
    if (gamesRequestInFlight.current) return;
    gamesRequestInFlight.current = true;
    setGames([])

    getQueuesAPI(
      "get-location-current-schedule",
      { 
        venue: authCtx.venue, // braves
        locationId: currentLocation.id, // 1
        measurement: currentLocation.measurement, // clubhouse
      },
      (data_, error) => {
        gamesRequestInFlight.current = false;
        if (error) {
          if (error.status === 401) {
            authCtx.logout();
            navigate("/auth");
          } else {
            alert("Unable to fetch data. Please refresh the page and try again.");
          }
        } else {
          // data_.push({
          //   dates: [
          //     {
          //       date: "2025-08-28",
          //       gameStartTime: "10:15",
          //       gatesOpen: "09:30",
          //       pregameStart: "08:00"
          //     }
          //   ],
          //   gamesInSeries: 1.0,
          //   teamID: 2.0,
          //   teamName: "Mets"
          // });
          // data_.push({
          //       dates: [
          //         {
          //           date: "2025-08-29",
          //           gameStartTime: "17:15",
          //           gatesOpen: "16:30",
          //           pregameStart: "10:00"
          //         },
          //         {
          //           date: "2025-08-30",
          //           gameStartTime: "17:15",
          //           gatesOpen: "16:30",
          //           pregameStart: "10:00"
          //         },
          //         {
          //           date: "2025-08-31",
          //           gameStartTime: "17:15",
          //           gatesOpen: "16:30",
          //           pregameStart: "10:00"
          //         },
          //         {
          //           date: "2025-09-01",
          //           gameStartTime: "17:15",
          //           gatesOpen: "16:30",
          //           pregameStart: "10:00"
          //         },
          //       ],
          //       gamesInSeries: 4.0,
          //       teamID: 2025.0,
          //       teamName: "All-Stars"
          //     });
          // Get the most recent game's year to set as current season
          const sortedGames = [...data_].sort((a, b) =>
            moment(b.dates[0].date).valueOf() - moment(a.dates[0].date).valueOf()
          );

          const mostRecentYear = moment(sortedGames[0]?.dates[0]?.date).format("YYYY");

          // Set current season and recent game only if nothing is selected yet
          if (!currentSeason) {
            setCurrentSeason(mostRecentYear);
            const adjustedNow = moment(now).tz(currentLocation.timeZone).subtract(3, 'hours').toDate();

            const { recentSeries, recentGame } = getMostRecentSeriesAndGame(data_, adjustedNow);
            if (recentGame) {
              const recentGameYear = moment(recentGame.date).format("YYYY");
              setCurrentSeason(recentGameYear); // Set season to match the recent game
            }
            setCurrentSeries(recentSeries);
            setCurrentGame(recentGame);
          }

          setGames(data_);
        }
      },
      authCtx.token
    );
  };

  // Start loading the store's map file now so it's ready when the data arrives
  // (RetailMap imports the same file; the browser reuses this download)
  useEffect(() => {
    if (currentLocation?.mapId && authCtx.league && authCtx.venue) {
      import(`../../clients/${authCtx.league}/${authCtx.venue}/maps/${currentLocation.mapId}.js`).catch(() => {});
    }
  }, [currentLocation?.mapId, authCtx.league, authCtx.venue]);

  // Fetch shared zone labels from Mongo on mount
  useEffect(() => {
    if (currentLocation) {
      getQueuesAPI(
        "get-retail-location-zone-labels",
        {
          measurement: currentLocation.measurement,
          venue: authCtx.venue,
          locationId: currentLocation.id,
        },
        (data, error) => {
          if (data) {
            setZoneLabels(data);
          }
        },
        authCtx.token
      );
    }
  }, [currentLocation]);

  const getGatesOpenTime = () => moment(`${currentGame?.date}T${currentGame?.gatesOpen}`);
  const getGameStartTime = () => moment(`${currentGame?.date}T${currentGame?.gameStartTime}`);

  const getCurrentMinute = () => Math.round((moment().tz(currentLocation.timeZone) - moment().tz(currentLocation.timeZone).startOf("day")) / 60000) - 1;

  const refreshRegularDay = async () => {
    try {
      await fetchData();
      setCurrentTime(Math.round((moment(now) - moment(now).startOf("day")) / 60000) - 1);
    } catch (error) {
      console.error('Error refreshing regular day:', error);
    }
  };

  const findLabelByZoneId = (zoneId) => {
    const label = zoneLabels[String(zoneId)];
    if (label) return label;
    return currentLocation.defaultZoneLabel;
  };
  
  useEffect(() => {
    if (data.length !== 0 && currentGame) {
      
      // Calculate time boundaries in minutes from start of day
      let gatesOpenTimeMinutes = Math.round(
        (getGatesOpenTime().toDate() - moment(currentGame.date).startOf("day").toDate()) / 60000
      ) - 1;
      let gameStartTimeMinutes = Math.round(
        (getGameStartTime().toDate() - moment(currentGame.date).startOf("day").toDate()) / 60000
      ) - 1;
  
      let nowCutoff = Math.round((moment().tz(currentLocation.timeZone) - moment().tz(currentLocation.timeZone).startOf("day")) / 60000)
      // Between midnight and 3 AM it's still the same store day (minutes 1440+), like everywhere
      // else in retail. Without this, live peak/average after midnight came out empty (NaN).
      if (moment().tz(currentLocation.timeZone).hour() < 3) nowCutoff += 1440;

      // console.log('Game times:');
      // console.log('  pregameStart:', currentGame.pregameStart);
      // console.log('  gatesOpen:', currentGame.gatesOpen, '(minute', gatesOpenTimeMinutes + ')');
      // console.log('  gameStartTime:', currentGame.gameStartTime, '(minute', gameStartTimeMinutes + ')');
      // console.log('  nowCutoff:', nowCutoff);
      // console.log('  isToday:', isToday);

      // Only calculate "Before Gates Open" if there's a pregame period
      const hasPregame = currentGame.pregameStart;
      let beforeGame, beforeGameAvg;
  
      if (hasPregame) {
        // Calculate "Before Gates Open" average from pregameStart to gates open
        const pregameStartTimeMinutes = Math.round(
          (moment(`${currentGame.date}T${currentGame.pregameStart}`).toDate() - moment(currentGame.date).startOf("day").toDate()) / 60000
        ) - 1;
        
        // beforeGame = data.slice(pregameStartTimeMinutes, gatesOpenTimeMinutes);
        const pregameEnd = isToday && nowCutoff < gatesOpenTimeMinutes
          ? nowCutoff
          : gatesOpenTimeMinutes;
        
        beforeGame = data.slice(pregameStartTimeMinutes, pregameEnd);
        
        beforeGameAvg = beforeGame.length > 0 
          ? Math.round(beforeGame.reduce((x, y) => x + y.total, 0) / beforeGame.length)
          : 0;
          
        // console.log('BEFORE GATES OPEN calculation:');
        // console.log('  pregameStartTimeMinutes:', pregameStartTimeMinutes);
        // console.log('  gatesOpenTimeMinutes:', gatesOpenTimeMinutes);
        // console.log('  beforeGame period length:', beforeGame.length, 'minutes');
        // console.log('  beforeGame time range:', beforeGame[0]?.time2, 'to', beforeGame[beforeGame.length - 1]?.time2);
        // console.log('  beforeGame total people:', beforeGame.reduce((sum, item) => sum + item.total, 0));
        // console.log('  beforeGameAvg:', beforeGameAvg);
      } else {
        // No pregame period - don't calculate this metric
        beforeGame = [];
        beforeGameAvg = null;
        // console.log('NO PREGAME PERIOD - skipping Before Gates Open calculation');
      }
  
      // Use intelligent cutoff with rounding from utils
      let rawIntelligentCutoff = findIntelligentCutoff(data, gatesOpenTimeMinutes);
      let intelligentCutoff = roundToNextHour(rawIntelligentCutoff);
      
      // For live games, don't go beyond current time
      // let midGameEnd = isToday 
      //   ? Math.min(nowCutoff, intelligentCutoff)
      //   : intelligentCutoff;

      let midGameEnd = isToday 
        ? nowCutoff
        : intelligentCutoff;
  
      let midGame = data.slice(gatesOpenTimeMinutes, midGameEnd);
      
      // console.log('AFTER GATES OPEN calculation:');
      // console.log('  gatesOpenTimeMinutes:', gatesOpenTimeMinutes);
      // console.log('  rawIntelligentCutoff:', rawIntelligentCutoff);
      // console.log('  intelligentCutoff (rounded):', intelligentCutoff);
      // console.log('  midGameEnd (final):', midGameEnd);
      // console.log('  midGame period length:', midGame.length, 'minutes');
      // console.log('  midGame time range:', midGame[0]?.time2, 'to', midGame[midGame.length - 1]?.time2);
      // console.log('  midGame total people:', midGame.reduce((sum, item) => sum + item.total, 0));
  
      let midGameAvg = Math.round(
        midGame.reduce((x, y) => x + y.total, 0) / midGame.length
      );
      
      // console.log('  midGameAvg:', midGameAvg);
  
      let filtered = data.slice(
        currentLocation.earliestOpenTime,
        isToday && currentLocation.earliestOpenTime < nowCutoff ? nowCutoff : undefined
      );
      
      // console.log('WHOLE DAY calculation:');
      // console.log('  earliestOpenTime:', currentLocation.earliestOpenTime);
      // console.log('  filtered period length:', filtered.length, 'minutes');
      // if (filtered.length > 0) {
      //   console.log('  filtered time range:', filtered[0]?.time2, 'to', filtered[filtered.length - 1]?.time2);
      //   console.log('  filtered total people:', filtered.reduce((sum, item) => sum + item.total, 0));
      //   console.log('  whole day avg:', Math.round(filtered.reduce((x, y) => x + y.total, 0) / filtered.length));
      // }
  
      let max = 0;
      let max2 = 0;
      let max3 = 0;
      let result = { preGame: null, startGame: null, wholeDay: null };
  
      // Only calculate preGame peak if we have pregame data
      if (hasPregame && beforeGame.length > 0) {
        for (let x = 0; x < beforeGame.length; x++) {
          if (beforeGame[x].perc > max) {
            max = beforeGame[x].perc;
            result.preGame = beforeGame[x];
          }
        }
        // console.log('BEFORE GATES OPEN peak:', result.preGame?.total, 'people (', result.preGame?.perc, '%) at', moment(result.preGame?.time2).format("LT"));
      }
  
      for (let x = 0; x < midGame.length; x++) {
        if (midGame[x].perc > max2) {
          max2 = midGame[x].perc;
          result.midGame = midGame[x];
        }
      }
      // console.log('AFTER GATES OPEN peak:', result.midGame?.total, 'people (', result.midGame?.perc, '%) at', moment(result.midGame?.time2).format("LT"));
  
      for (let x = 0; x < filtered.length; x++) {
        if (filtered[x].perc > max3) {
          max3 = filtered[x].perc;
          result.wholeDay = filtered[x];
        }
      }
      // console.log('WHOLE DAY peak:', result.wholeDay?.total, 'people (', result.wholeDay?.perc, '%) at', moment(result.wholeDay?.time2).format("LT"));
  
      const avgResult = dayType === currentLocation.dayTypes.GAME
        ? { 
            preGame: hasPregame ? beforeGameAvg : null, // Only show if pregame exists
            startGame: midGameAvg 
          }
        : {
            wholeDay: Math.round(
              filtered.reduce((x, y) => x + y.total, 0) / filtered.length
            ),
          };
          
      // console.log('FINAL AVERAGES:', avgResult);
      // console.log('=== END CALCULATION ===');
  
      setPeak(result);
      setAvg(avgResult);
    }
  }, [data, currentGame, dayType, isToday, currentLocation]);

  useEffect(() => {
    if (!id) {
      navigate("/locations");
    }
  }, [id, navigate]);
  
  // The day being viewed, as a string, so setting the same date again doesn't refetch.
  // Switching day type (game / regular / overview) bumps fetchNonce so the data is always
  // reloaded and processed for the new day type, even when the date stays the same.
  const selectedDateKey = selectedDate ? moment(selectedDate).format('YYYY-MM-DD') : null;

  useEffect(() => {
    if (currentLocation && selectedDate && currentGame) {
      // Only fetch if currentGame date and selectedDate are different
      if (dayType === currentLocation.dayTypes.GAME && 
          moment(selectedDate).format('YYYY-MM-DD') !== moment(currentGame.date).format('YYYY-MM-DD')) {
        // console.log('Date mismatch detected! Updating selectedDate without fetching');
        setSelectedDate(moment(currentGame.date).toDate());
        return; // Don't fetch yet
      }
      
      // console.log('Starting data fetch process');
      const loadData = async () => {
        try {
          await fetchData();
          setLoading(false);
          // console.log('Data fetch and processing complete');
        } catch (error) {
          console.error('Error in data fetch process:', error);
        }
      };
  
      loadData();
    } 
  }, [currentLocation, selectedDateKey, currentGame, fetchNonce]);
  
  useEffect(() => {
    if (currentGame && dayType === currentLocation.dayTypes.GAME) {
      // console.log('Syncing selectedDate with currentGame date');
      setSelectedDate(moment(currentGame.date).toDate());
    }
  }, [currentGame, dayType]);
  
  // This handles selecting the game date and series when switching between reg and game day
  // hanldes setup for when day type is GAME and REGULAR
  // ensures the correction section is set when a retail location is only counter (maxOccupancy = 0)
  // Note counter only locations have only COUNTER and all other locations will be MAP COUNTER CHARTS
  useEffect(() => {
    // Reload for the new day type (the date alone may not change)
    setFetchNonce((n) => n + 1);
    if (dayType === currentLocation.dayTypes.GAME) {
      // For a retail location that only has counters (no occupancy) we set to counter type
      setCurrentSection(currentLocation.maxOccupancy === 0 ? currentLocation.sectionTypes.COUNTER : currentLocation.sectionTypes.MAP);
      // Only reset these if we're switching TO game day
      if (!currentGame) {  // Only reset if no game is selected
        setSelectedDate(null);
        setCurrentSeries(null);
        setCurrentGame(null);
      }
      // If we do have a game make sure to set the selectedDate to that game's date
      setSelectedDate(moment(currentGame?.date).toDate());
      setShowCalendar(false);
    } else {
      let allDates = [];
      let lastNonGame = [];
      for (let x = 0; x < games.length; x++) {
        for (let y = 0; y < games[x].dates.length; y++) {
          let curr = moment(games[x].dates[y].date).startOf("day");
          if (moment(now).startOf("day").toDate() >= curr.toDate()) {
            allDates.push(curr.format());
          }
        }
      }

      allDates.sort((x, y) => moment(y).toDate() - moment(x).toDate());

      for (let z = 0; z < 30; z++) {
        if (!allDates.includes(moment(now).startOf("day").subtract(z, "days").format())) {
          lastNonGame.push(moment(now).startOf("day").subtract(z, "days").format());
        }
      }

      setSelectedDate(moment(lastNonGame[0]).toDate());

      setCurrentTime(
        Math.round((moment().tz(currentLocation.timeZone) - moment().tz(currentLocation.timeZone).startOf("day")) / 60000) - 1
      );
      // When switching to regular reset the section type to map
      // dayType === currentLocation.dayTypes.REGULAR && setCurrentSection(currentLocation.sectionTypes.MAP);
      // No need to check day type we assume else is regular day (can only ever be game and regular day)
      // For a retail location that only has counters (no occupancy) we set to counter type
      setCurrentSection(currentLocation.maxOccupancy === 0 ? currentLocation.sectionTypes.COUNTER : currentLocation.sectionTypes.MAP);
    }
  }, [dayType]);

  // Important for initially setting the slider position (Clubhousemap or whatever component RetailMap loads has the final say)
  useEffect(() => {
    if (currentGame) {
      if (isToday) {
        setCurrentTime(
          isToday
            ? Math.round((moment().tz(currentLocation.timeZone) - moment().tz(currentLocation.timeZone).startOf("day")) / 60000) - 1
            : currentLocation.doorsOpenTime
        );
        setSelectedDate(moment(currentGame?.date).toDate());
      } else {
        setCurrentTime(currentLocation.doorsOpenTime);
      }
    }
  }, [currentGame]);

  // Keep now updated
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(
        new Date(new Date().toLocaleString('en-US', { timeZone: currentLocation.timeZone }))
      );
    }, 60 * 1000);

    return () => clearInterval(interval);
  }, [currentLocation.timeZone]);

  useEffect(() => {
    const nowMoment = moment(now);
    const adjustedNow = nowMoment.hour() < 3
      ? nowMoment.subtract(1, "day").startOf("day")
      : nowMoment.startOf("day");
  
    const gameDateMatch =
      dayType === currentLocation.dayTypes.GAME &&
      currentGame?.date &&
      moment(currentGame.date).startOf("day").isSame(adjustedNow);
  
    const regularDateMatch =
      dayType === currentLocation.dayTypes.REGULAR &&
      selectedDate &&
      moment(selectedDate).startOf("day").isSame(adjustedNow);
  
    setIsToday(gameDateMatch || regularDateMatch);
  }, [dayType, currentGame, selectedDate, now]);

  useEffect(() => {
    dayType === currentLocation.dayTypes.GAME && getGames();
  }, [dayType]);

  // The schedule request doesn't depend on the season (seasons filter it on screen),
  // so only fetch here when nothing has been loaded yet
  useEffect(() => {
    if (games.length === 0) getGames();
  }, [currentSeason]);

  return children({
    loading,
    data,
    games,
    zones,
    now,
    currentLocation,
    currentSeries,
    currentGame,
    currentSection,
    currentSeason,
    currentTime,
    dayType,
    selectedDate,
    showCalendar,
    peak,
    avg,
    sort,
    showAllMaps,
    isToday,
    autoFetchEnabled,
    occupancyAutoFetchEnabled,
    occupancyAutoFetchDidComplete,
    backgroundLoading,
    regularDayCount,
    counterDataAvailable,
    pregameCount,
    gatesOpenCount,
    firstPitchCount,
    counterSensorInfoData,
    counterTrafficDataList,
    overviewTrafficDataList,
    overviewSelectedDates,
    overviewGameTitlesList,
    queueTimeData,
    queueTimeDataMiniMap,
    transactionTimeData,
    transactionTimeDataMiniMap,
    zoneLabels,

    // Actions
    setLoading,
    setCurrentSection,
    setCurrentSeason,
    setDayType,
    setSelectedDate,
    setShowCalendar,
    setCurrentGame,
    setCurrentSeries,
    setCurrentTime,
    setSort,
    setShowAllMaps,
    setAutoFetchEnabled,
    setOccupancyAutoFetchEnabled,
    setOccupancyAutoFetchDidComplete,
    setZoneLabels,

    // Methods
    fetchData,
    getGames,
    getGatesOpenTime,
    getGameStartTime,
    getCurrentMinute,
    findLabelByZoneId,
    refreshRegularDay,

    // Context and navigation
    authCtx,
    navigate,
    theme
  });
};

export default RetailLocationController;